import { useEffect, useRef } from 'react';

const useNavigationBlocker = (enabled: boolean = true): void => {
  const isBlockingRef = useRef(false);
  const lockedLocationRef = useRef(window.location.href);

  useEffect(() => {
    if (!enabled) return;

    lockedLocationRef.current = window.location.href;
    isBlockingRef.current = true;

    // "Trap" Strategy:
    // Push a new state state to trap the user in the current page
    window.history.pushState({ locked: true }, '', lockedLocationRef.current);

    const handlePopState = (event: PopStateEvent): void => {
      if (!isBlockingRef.current) return;
      // User pressed Back -> Browser went back -> We push Forward immediately.
      window.history.pushState({ locked: true }, '', lockedLocationRef.current);
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent): string => {
      if (!isBlockingRef.current) return '';
      event.preventDefault();
      // Browsers show their own generic message, but we must set returnValue
      const message = 'Exam in progress';
      event.returnValue = message;
      return message;
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!isBlockingRef.current) return;
      
      const isMod = event.ctrlKey || event.metaKey;
      const activeTag = document.activeElement?.tagName;
      const isInput = activeTag === 'INPUT' || activeTag === 'TEXTAREA' || (document.activeElement as HTMLElement)?.isContentEditable;

      if (
        // Block Alt+Arrows (Back/Forward)
        (event.altKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) ||
        // Block Cmd/Ctrl + [ or ] or Arrows (Browser Nav)
        (isMod && (event.key === '[' || event.key === ']' || event.key === 'ArrowLeft' || event.key === 'ArrowRight')) ||
        // Block Backspace (unless in input)
        (event.key === 'Backspace' && !isInput) ||
        // Block Refresh
        event.key === 'F5' ||
        (isMod && (event.key === 'r' || event.key === 'R'))
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const handleMouseBlock = (event: MouseEvent): void => {
      if (!isBlockingRef.current) return;
      if (event.button === 3 || event.button === 4) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    
    const handleContextMenu = (event: MouseEvent): boolean => {
      if (!isBlockingRef.current) return true;
      event.preventDefault();
      return false;
    };

    // Add Listeners
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseBlock);
    document.addEventListener('mouseup', handleMouseBlock);
    document.addEventListener('contextmenu', handleContextMenu);

    // Cleanup
    return (): void => {
      isBlockingRef.current = false;
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseBlock);
      document.removeEventListener('mouseup', handleMouseBlock);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [enabled]);
};

export default useNavigationBlocker;